import React, { useState } from 'react';
import {
  PdsModal,
  PdsModalHeader,
  PdsModalBody,
  PdsModalFooter,
  PdsBox,
  PdsMantineText,
  PdsMantineButton,
  PdsCheckbox,
  PdsCard,
  PdsSpinnerIcon,
  PdsControlPlayIcon,
  PdsSyncIcon,
} from '@podium-design-system/react-components';

const TOOLS = [
  {
    key: 'securitytrails',
    label: 'SecurityTrails',
    description: 'Comprehensive domain intelligence and threat analysis',
    defaultChecked: true,
  },
  {
    key: 'crtsh',
    label: 'Crt.sh',
    description: 'Certificate transparency logs and SSL certificate analysis',
    defaultChecked: true,
  },
  {
    key: 'urlscan',
    label: 'URLScan.io (Scan)',
    description: 'Website scanning and behavioral analysis',
    defaultChecked: false,
  },
  {
    key: 'urlscanSearch',
    label: 'URLScan.io (Search)',
    description: 'Search historical scan data without performing new scans',
    defaultChecked: false,
  },
  {
    key: 'publicwww',
    label: 'PublicWWW',
    description: 'Source code search across websites referencing the target domain',
    defaultChecked: false,
  },
];

const InvestigationModal = ({ isOpen, onClose, onInvestigate, targetDomain, isRerunMode = false }) => {
  const [selectedTools, setSelectedTools] = useState({
    securitytrails: true,
    crtsh: true,
    urlscan: false,
    urlscanSearch: false,
    publicwww: false,
  });
  const [publicWwwExactKeyword, setPublicWwwExactKeyword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleToolChange = (tool, checked) => {
    setSelectedTools(prev => ({ ...prev, [tool]: checked }));
    if (tool === 'publicwww' && !checked) {
      setPublicWwwExactKeyword(false);
    }
  };

  const handleExactKeywordChange = (checked) => {
    const on = Boolean(checked);
    setPublicWwwExactKeyword(on);
    if (on) {
      setSelectedTools(prev => ({ ...prev, publicwww: true }));
    }
  };

  const handleSubmit = async () => {
    if (!Object.values(selectedTools).some(Boolean)) {
      alert('Please select at least one tool.');
      return;
    }
    setIsLoading(true);
    try {
      await onInvestigate(targetDomain, {
        ...selectedTools,
        publicWwwExactKeyword: Boolean(selectedTools.publicwww && publicWwwExactKeyword),
      });
      onClose();
    } catch (error) {
      console.error('Investigation failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const hasSelection = Object.values(selectedTools).some(Boolean);

  return (
    <PdsModal
      show={isOpen}
      onDismiss={onClose}
      minWidth="420px"
      maxWidth="520px"
      minHeight="auto"
      maxHeight="90vh"
    >
      <PdsModalHeader
        headline={isRerunMode ? 'Re-run tools options' : 'Node Options'}
      />
      <PdsModalBody>
        <PdsBox direction="column" gap="md">
          <PdsMantineText type="body" color="neutral">
            {isRerunMode
              ? `Select tools to re-run for: ${targetDomain}`
              : `Select tools for: ${targetDomain}`}
          </PdsMantineText>

          <PdsBox direction="column" gap="xs">
            {TOOLS.map(({ key, label, description }) => (
              <PdsCard
                key={key}
                border
                borderRadius="sm"
                padding="sm"
                direction="column"
                gap="sm"
                color={selectedTools[key] ? 'action' : 'neutral'}
                contrast={selectedTools[key] ? 'low' : 'min'}
              >
                <PdsBox direction="row" alignItems="center" gap="sm">
                  <PdsCheckbox
                    checked={selectedTools[key]}
                    color="action"
                    onCheckedChange={(checked) => handleToolChange(key, checked)}
                  />
                  <PdsBox direction="column" gap="xs">
                    <PdsMantineText type="textline" fontWeight="strong" component="span">
                      {label}
                    </PdsMantineText>
                    <PdsMantineText type="textline" fontSize="300" color="neutral" component="span">
                      {description}
                    </PdsMantineText>
                  </PdsBox>
                </PdsBox>
                {key === 'publicwww' && (
                  <PdsBox direction="row" alignItems="center" gap="sm" style={{ paddingLeft: 28 }}>
                    <PdsCheckbox
                      checked={publicWwwExactKeyword}
                      color="action"
                      onCheckedChange={handleExactKeywordChange}
                    />
                    <PdsMantineText type="textline" fontSize="300" color="neutral" component="span">
                      exact keyword
                    </PdsMantineText>
                  </PdsBox>
                )}
              </PdsCard>
            ))}
          </PdsBox>
        </PdsBox>
      </PdsModalBody>
      <PdsModalFooter>
        <PdsBox direction="row" justifyContent="end" gap="sm">
          <PdsMantineButton
            rank="outline"
            color="neutral"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </PdsMantineButton>
          <PdsMantineButton
            rank="fill"
            color="action"
            onClick={handleSubmit}
            disabled={isLoading || !hasSelection}
            leadingIcon={isLoading ? <PdsSpinnerIcon /> : isRerunMode ? <PdsSyncIcon /> : <PdsControlPlayIcon />}
          >
            {isLoading
              ? isRerunMode ? 'Re-running...' : 'Starting...'
              : isRerunMode ? 'Re-run tools' : 'Start Investigation'}
          </PdsMantineButton>
        </PdsBox>
      </PdsModalFooter>
    </PdsModal>
  );
};

export default InvestigationModal;
